import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Shield, Search, Plus, Settings, UserCheck, Trash2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface AdminUser {
  id: string;
  user_id: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  permissions: Record<string, boolean> | null;
}

const DEFAULT_PERMISSIONS = {
  manage_users: false,
  manage_content: true,
  manage_settings: false,
  view_analytics: true,
  manage_referrals: false,
};

const PERMISSION_LABELS: Record<string, string> = {
  manage_users: 'Manage Users',
  manage_content: 'Manage Content',
  manage_settings: 'Manage Settings',
  view_analytics: 'View Analytics',
  manage_referrals: 'Manage Referrals',
};

export default function AdminUsers() {
  const { toast } = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState('viewer');
  const [formActive, setFormActive] = useState(true);
  const [formPermissions, setFormPermissions] = useState<Record<string, boolean>>(DEFAULT_PERMISSIONS);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Email lookup cache
  const [emailMap, setEmailMap] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);

      // Fetch emails for all admin users
      if (data && data.length > 0) {
        const { data: profiles } = await supabase
          .rpc('get_admin_emails', { user_ids: data.map(u => u.user_id) })
          .single();

        // If rpc doesn't exist, we just show user_ids, that's fine
        if (profiles && typeof profiles === 'object') {
          setEmailMap(profiles as Record<string, string>);
        }
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingUser(null);
    setFormEmail('');
    setFormRole('viewer');
    setFormActive(true);
    setFormPermissions({ ...DEFAULT_PERMISSIONS });
    setDialogOpen(true);
  };

  const openEditDialog = (user: AdminUser) => {
    setEditingUser(user);
    setFormEmail(emailMap[user.user_id] || user.user_id);
    setFormRole(user.role);
    setFormActive(user.is_active);
    setFormPermissions(
      (user.permissions as Record<string, boolean>) || { ...DEFAULT_PERMISSIONS }
    );
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingUser) {
        // Update existing admin
        const { error } = await supabase
          .from('admin_users')
          .update({
            role: formRole,
            is_active: formActive,
            permissions: formPermissions,
          })
          .eq('id', editingUser.id);

        if (error) throw error;
        toast({ title: 'Admin updated', description: `Role set to ${formRole}` });
      } else {
        // Add new admin, look up user by email first
        const email = formEmail.trim().toLowerCase();
        if (!email) {
          toast({ title: 'Email required', variant: 'destructive' });
          setSaving(false);
          return;
        }

        // Look up user_id from auth.users via a server function or direct query
        const { data: lookupData, error: lookupError } = await supabase
          .rpc('lookup_user_by_email', { lookup_email: email });

        let userId: string | null = null;

        if (lookupError || !lookupData) {
          // Fallback: if email looks like a UUID, use it directly
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (uuidRegex.test(email)) {
            userId = email;
          } else {
            toast({
              title: 'User not found',
              description: `No account found for ${email}. They must sign up first, or paste their user ID directly.`,
              variant: 'destructive',
            });
            setSaving(false);
            return;
          }
        } else {
          userId = lookupData;
        }

        // Check if already an admin
        const existing = users.find(u => u.user_id === userId);
        if (existing) {
          toast({
            title: 'Already an admin',
            description: 'This user already has an admin record. Edit it instead.',
            variant: 'destructive',
          });
          setSaving(false);
          return;
        }

        const { error } = await supabase
          .from('admin_users')
          .insert({
            user_id: userId,
            role: formRole,
            is_active: formActive,
            permissions: formPermissions,
          });

        if (error) throw error;
        toast({ title: 'Admin added', description: `${email} added as ${formRole}` });
      }

      setDialogOpen(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Error saving admin:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save admin user',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', deleteTarget.id);

      if (error) throw error;
      toast({ title: 'Admin removed', description: 'Admin access has been revoked.' });
      setDeleteTarget(null);
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting admin:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove admin',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const togglePermission = (key: string) => {
    setFormPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredUsers = users.filter(user => {
    const email = emailMap[user.user_id] || '';
    const matchesSearch =
      user.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const getRoleBadge = (role: string) => {
    const variants = {
      'admin': 'destructive',
      'editor': 'default',
      'moderator': 'default',
      'viewer': 'secondary'
    } as const;

    return <Badge variant={variants[role as keyof typeof variants] || 'secondary'}>{role}</Badge>;
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <Badge variant={isActive ? 'default' : 'secondary'}>
        {isActive ? 'Active' : 'Inactive'}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Users</h1>
          <p className="text-muted-foreground">
            Manage administrator accounts and permissions
          </p>
        </div>
        <Button onClick={openAddDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add Admin
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Admins</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{users.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(u => u.is_active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Super Admins</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(u => u.role === 'admin').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Viewers</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(u => u.role === 'viewer').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Users</CardTitle>
          <CardDescription>Search and filter admin users</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email, user ID, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="moderator">Moderator</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Admin Users ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div>
                      {emailMap[user.user_id] && (
                        <div className="text-sm font-medium">{emailMap[user.user_id]}</div>
                      )}
                      <div className="font-mono text-xs text-muted-foreground">
                        {user.user_id.substring(0, 8)}...
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>{getStatusBadge(user.is_active)}</TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {user.permissions && Object.keys(user.permissions).length > 0
                        ? Object.entries(user.permissions)
                            .filter(([, v]) => v)
                            .map(([k]) => PERMISSION_LABELS[k] || k)
                            .join(', ') || 'None'
                        : 'Default permissions'
                      }
                    </div>
                  </TableCell>
                  <TableCell>
                    {format(new Date(user.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(user)}>
                        <Settings className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(user)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredUsers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No admin users found matching your criteria
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit Admin User' : 'Add Admin User'}</DialogTitle>
            <DialogDescription>
              {editingUser
                ? 'Update this admin user\'s role, status, and permissions.'
                : 'Add a new admin by entering their email address or user ID. They must have an existing account.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email or User ID</Label>
                <Input
                  id="admin-email"
                  placeholder="user@example.com or UUID"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                />
              </div>
            )}

            {editingUser && (
              <div className="space-y-2">
                <Label>User</Label>
                <div className="text-sm text-muted-foreground">
                  {emailMap[editingUser.user_id] || editingUser.user_id}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formRole} onValueChange={setFormRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                  <SelectItem value="editor">Editor (content management)</SelectItem>
                  <SelectItem value="viewer">Viewer (read-only)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="admin-active"
                checked={formActive}
                onCheckedChange={(checked) => setFormActive(checked === true)}
              />
              <Label htmlFor="admin-active">Active</Label>
            </div>

            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="space-y-2 rounded-md border p-3">
                {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2">
                    <Checkbox
                      id={`perm-${key}`}
                      checked={formPermissions[key] ?? false}
                      onCheckedChange={() => togglePermission(key)}
                    />
                    <Label htmlFor={`perm-${key}`} className="text-sm font-normal">
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingUser ? 'Save Changes' : 'Add Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Admin Access</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently revoke admin access for{' '}
              <strong>{deleteTarget && (emailMap[deleteTarget.user_id] || deleteTarget.user_id)}</strong>.
              They will no longer be able to access the admin dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove Admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
